import React from 'react';

import { IAdserverCampaign, IFlightDateCheckpoint } from '~/src/dash/api/campaign/campaign';
import {
  getCampaign,
  IAdserverCampaignTracker,
  IAdserverCreative,
  ISiteAdserverCampaignAssociation,
} from '~/src/dash/api/console-api';
import { IFrequencyCap } from '~/src/dash/api/frequency-cap';
import { EBool } from '~/src/dash/api/types/bool';
import { IStore } from '~/src/dash/reducers/store';
import {
  extractRouteParams,
  navigate,
  parseLocation,
  RouteCampaignSettingsList,
} from '~/src/dash/router/routes';
import {
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { getCreativeType } from '~/src/dash/utils/campaign';
import {
  formatDateFromUTCToOffset,
  getFutureEndDate,
  getFutureStartDate,
  getToday,
} from '~/src/dash/utils/date';
import { decorate } from '~/src/dash/utils/decorator';
import { CampaignFormView } from '~/src/dash/views/campaign/campaign-form-view';
import { LoadingView } from '~/src/dash/views/loading-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';
interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}
interface IState {
  campaign?: IAdserverCampaign;
  snackbarMessage: string;
  snackbarError: boolean;
}

const initialState = {
  ID: '',
  UUID: '',
  CreatedAt: '',
  UpdatedAt: '',
  DeletedAt: '',
};

export const CampaignCopyView = decorate(
  {
    redux: (state: IStore) => {
      return {
        session: state.session,
        user: state.user,
      };
    },
  },
  class extends React.Component<IProps, IState> {
    state = {
      campaign: undefined,
      snackbarMessage: '',
      snackbarError: false,
    };

    componentDidMount() {
      const loc = parseLocation(this.props.location.pathname);
      getCampaign({
        query: {
          publisher_id: this.props.session.publisher.ID,
          campaign_id: loc.campaign,
        },
      })
        .then((campaign: IAdserverCampaign) => {
          // alter start date, endDate if before currentDate
          const validatedCampaign = this.setCampaignCloneStartEndDates(campaign);

          this.setState({
            campaign: validatedCampaign,
          });
        })
        .catch(() => {
          this.setState(
            {
              snackbarMessage:
                "You don't have access to this campaign. Please create one or select one in your existiing list.",
              snackbarError: true,
            },
            () => {
              setTimeout(() => {
                navigate(
                  RouteCampaignSettingsList,
                  extractRouteParams({
                    session: this.props.session,
                    user: this.props.user,
                  }),
                );
              }, 3000);
            },
          );
        });
    }

    setCampaignCloneStartEndDates = (campaign: IAdserverCampaign) => {
      const startDateToCheck = formatDateFromUTCToOffset(
        campaign.StartDate,
        this.props.session.publisher.Timezone,
      );
      const endDateToCheck = formatDateFromUTCToOffset(
        campaign.EndDate,
        this.props.session.publisher.Timezone,
      );
      const todayDate = getToday(this.props.session.publisher.Timezone);
      if (startDateToCheck.isBefore(todayDate)) {
        campaign.StartDate = getFutureStartDate(
          this.props.session.publisher.Timezone || 'UTC',
        ).format('YYYY-MM-DDTHH:mm:ssZ');
      }
      // in case of campaign.StartDate changed to the same day as campaign.EndDate, we want to update the campaign.EndDate as well
      const newCampaignStartDate = formatDateFromUTCToOffset(
        campaign.StartDate,
        this.props.session.publisher.Timezone,
      );

      if (endDateToCheck.isBefore(newCampaignStartDate)) {
        campaign.EndDate = getFutureEndDate(
          this.props.session.publisher.Timezone || 'UTC',
          6,
          'day',
        ).format('YYYY-MM-DDTHH:mm:ssZ');
      }

      // filter out invalid checkpoints
      campaign.FlightDateCheckpoints = campaign.FlightDateCheckpoints.filter((checkpoint) => {
        const checkpointStartDate = formatDateFromUTCToOffset(
          checkpoint.StartDate,
          this.props.session.publisher.Timezone,
        );
        const checkpointEndDate = formatDateFromUTCToOffset(
          checkpoint.EndDate,
          this.props.session.publisher.Timezone,
        );
        if (
          checkpointStartDate.isBefore(newCampaignStartDate) ||
          checkpointEndDate.isBefore(newCampaignStartDate)
        ) {
          // remove checkpoints with start/end dates before the campaign start date
          return false;
        }
        return true;
      });

      if (campaign.FlightDateCheckpoints.length === 0) {
        campaign.FlightDateCheckpointsEnabled = EBool.EBool_FALSE;
      }
      return campaign;
    };

    snackbarClose = () => {
      this.setState({
        snackbarMessage: '',
      });
    };

    render() {
      const campaign = this.state.campaign || ({} as IAdserverCampaign);
      return (
        <div>
          {campaign.ID ? (
            <CampaignFormView
              campaign={Object.assign({}, this.state.campaign, {
                ID: '',
                UUID: '',
                CRONQuantityCount: 0,
                CreatedAt: '',
                UpdatedAt: '',
                DeletedAt: '',
                Name: campaign.Name + ' - copy',
                AdserverCampaignTrackers: campaign.AdserverCampaignTrackers.map(
                  (tracker: IAdserverCampaignTracker) => {
                    // before new adserver_creatives is created, tracker is only allowed to target campaign level
                    return Object.assign({}, tracker, initialState, { CreativeID: 0 });
                  },
                ),
                SiteAdserverCampaignAssociations: campaign.SiteAdserverCampaignAssociations.map(
                  (asso: ISiteAdserverCampaignAssociation) => {
                    asso.ID = 0;
                    asso.AdserverCampaignID = 0;
                    return Object.assign({}, asso);
                  },
                ),
                AdserverCreatives: campaign.AdserverCreatives.map((creative: IAdserverCreative) => {
                  creative.ID = '0';
                  creative.Name = creative.Name + ' - copy';
                  return Object.assign({}, creative, initialState);
                }),
                FrequencyCaps: campaign.FrequencyCaps.map((frequencyCap: IFrequencyCap) => {
                  frequencyCap.ID = 0;
                  frequencyCap.AdserverCampaignID = NaN;
                  frequencyCap.CreatedAt = '';
                  frequencyCap.DeletedAt = '';
                  frequencyCap.UpdatedAt = '';
                  return Object.assign({}, frequencyCap);
                }),
                FlightDateCheckpoints: campaign.FlightDateCheckpoints.map(
                  (flightDateCheckpoint: IFlightDateCheckpoint) => {
                    flightDateCheckpoint.ID = 0;
                    flightDateCheckpoint.AdserverCampaignID = NaN;
                    flightDateCheckpoint.CreatedAt = '';
                    flightDateCheckpoint.DeletedAt = '';
                    flightDateCheckpoint.UpdatedAt = '';
                    return Object.assign({}, flightDateCheckpoint);
                  },
                ),
              })}
              creativeType={getCreativeType(this.state.campaign)}
            />
          ) : (
            <LoadingView active={true} text='Loading the campaign' />
          )}
          <SnackbarView
            open={!!this.state.snackbarMessage}
            error={!!this.state.snackbarError}
            onClose={this.snackbarClose}
          >
            {this.state.snackbarMessage}
          </SnackbarView>
        </div>
      );
    }
  },
);
